import { useRef } from 'react'

interface UseFirstLoadResolutionArgs {
  // Raw `getFetchError` value (the slice stores the original error object).
  // Any truthy value means failed.
  data: unknown
  fetchFailed: unknown
}

interface FirstLoadResolution {
  isInitialLoad: boolean
  hasInitialLoadError: boolean
}

/**
 * Sticky-latch first-fetch resolution for polled resources. Once data
 * arrives, both flags stay false. Once an error is seen pre-data,
 * hasInitialLoadError stays true until data arrives — so the per-poll
 * `error: undefined` reset in startFetch doesn't flicker the error UI.
 */
export const useFirstLoadResolution = ({
  data,
  fetchFailed,
}: UseFirstLoadResolutionArgs): FirstLoadResolution => {
  const hasDataArrivedRef = useRef(false)
  const hasErroredRef = useRef(false)

  if (data !== null) {
    hasDataArrivedRef.current = true
  }
  if (Boolean(fetchFailed) && !hasDataArrivedRef.current) {
    hasErroredRef.current = true
  }

  const isInitialLoad = !hasDataArrivedRef.current && !hasErroredRef.current
  const hasInitialLoadError =
    !hasDataArrivedRef.current && hasErroredRef.current

  return { isInitialLoad, hasInitialLoadError }
}
